import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Calendar, CheckSquare, DollarSign, Users } from 'lucide-react';
import { useAgentStatus } from '../../hooks/useAgentIntegration.ts';
import { useChatStore } from './chatStore.ts';

export const AgentStatusIndicator: React.FC = () => {
  const { isAgentActive, lastActionTime } = useAgentStatus();
  const { pendingActions } = useChatStore();

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'calendar':
        return <Calendar className="w-3 h-3" />;
      case 'task':
        return <CheckSquare className="w-3 h-3" />;
      case 'budget':
        return <DollarSign className="w-3 h-3" />;
      case 'vendor':
        return <Users className="w-3 h-3" />;
      default:
        return <Zap className="w-3 h-3" />;
    }
  };

  const hasPendingActions = pendingActions.length > 0;
  const showIndicator = isAgentActive || hasPendingActions;

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="px-3 py-2 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg mb-3"
        >
          <div className="flex items-center space-x-2">
            {/* Agent Activity Indicator */}
            <div className="flex items-center space-x-2">
              <motion.div
                animate={{ rotate: isAgentActive ? 360 : 0 }}
                transition={{ duration: 1, repeat: isAgentActive ? Infinity : 0, ease: "linear" }}
                className="bg-gradient-to-r from-pink-500 to-purple-500 p-1 rounded-full"
              >
                <Zap className="w-3 h-3 text-white" />
              </motion.div>
              
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-700">
                  {isAgentActive ? 'Agent is working...' : 'Agent Actions'}
                </div>
                
                {/* Action Queue */}
                {hasPendingActions && (
                  <div className="flex items-center space-x-1 mt-1">
                    {pendingActions.slice(0, 3).map((action, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                          action.executed 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-pink-100 text-pink-600'
                        }`}
                      >
                        {getActionIcon(action.type)}
                        <span className="capitalize">{action.type}</span>
                        {action.executed && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 bg-green-500 rounded-full"
                          />
                        )}
                      </motion.div>
                    ))}
                    
                    {pendingActions.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{pendingActions.length - 3} more
                      </div>
                    )}
                  </div>
                )}
                
                {/* Last Action Time */}
                {lastActionTime && !isAgentActive && (
                  <div className="text-xs text-gray-500 mt-1">
                    Last updated: {lastActionTime.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          {isAgentActive && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 2 }}
              className="h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mt-2"
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}; 